import React from 'react';
import PropTypes from 'prop-types';
import { Avatar, Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { Stethoscope } from 'lucide-react';
import { formatCurrency, formatShortDate } from '../formatters';

function ProductionItem({ item }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1.1, pl: 2.5, pr: 1.5 }}>
      <Stethoscope size={16} color="currentColor" style={{ flexShrink: 0, opacity: 0.5 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {item.procedureName}
          {item.tooth ? ` · dente ${item.tooth}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {item.subtitle}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="body2">{item.amountFormatted}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatShortDate(item.executionDate)}
        </Typography>
      </Box>
    </Stack>
  );
}

ProductionItem.propTypes = {
  item: PropTypes.shape({
    procedureName: PropTypes.string,
    subtitle: PropTypes.string,
    tooth: PropTypes.string,
    amountFormatted: PropTypes.string,
    executionDate: PropTypes.string,
  }).isRequired,
};

export default function ClinicalProductionGroupList({
  groups,
  proceduresCount,
  hasHiddenItems,
  onShowAllItems,
}) {
  if (groups.length === 0) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 3, p: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Nenhum procedimento encontrado para o período e a situação selecionados.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, px: 0.5 }}
      >
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          Procedimentos
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {proceduresCount} itens
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {groups.map((group, groupIndex) => (
          <Box key={group.key}>
            {groupIndex > 0 && <Box sx={{ height: 6, bgcolor: 'action.hover' }} />}

            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ p: 1.25, bgcolor: 'action.hover' }}
            >
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  fontSize: 11,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                }}
              >
                {group.initials}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                  {group.title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {group.subtitle}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>
                {formatCurrency(group.total)}
              </Typography>
            </Stack>

            {group.items.map((item, itemIndex) => (
              <Box key={item.id}>
                {itemIndex > 0 && <Divider sx={{ ml: 2.5 }} />}
                <ProductionItem item={item} />
              </Box>
            ))}
          </Box>
        ))}

        {hasHiddenItems && (
          <>
            <Divider />
            <Box sx={{ textAlign: 'center', py: 0.5 }}>
              <Button size="small" onClick={onShowAllItems}>
                Ver todos os {proceduresCount} procedimentos
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </>
  );
}

ClinicalProductionGroupList.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string.isRequired,
      initials: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
      items: PropTypes.array.isRequired,
    }),
  ).isRequired,
  proceduresCount: PropTypes.number.isRequired,
  hasHiddenItems: PropTypes.bool.isRequired,
  onShowAllItems: PropTypes.func.isRequired,
};
